import { performanceMonitor } from './performanceMonitor';
import { smartCacheManager } from './smartCacheManager';
import { backgroundSyncService } from './backgroundSyncService';
import { predictivePreloader } from './predictivePreloader';
import { debugLog } from '../utils/debugLog';

interface Enhancement {
  id: string;
  name: string;
  condition: () => boolean;
  apply: () => void;
  rollback: () => void;
  priority: number;
  isActive: boolean;
}

interface SystemCapabilities {
  memory: 'low' | 'medium' | 'high';
  network: 'slow' | 'fast';
  device: 'mobile' | 'desktop';
  performance: 'poor' | 'good' | 'excellent';
}

class ProgressiveEnhancement {
  private enhancements: Enhancement[] = [];
  private capabilities: SystemCapabilities;
  private monitoringInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Wait for DOM to be ready
    if (typeof window !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize());
      } else {
        setTimeout(() => this.initialize(), 100);
      }
    }
  }

  private initialize() {
    try {
      this.capabilities = this.detectCapabilities();
      this.initializeEnhancements();
      this.startMonitoring();
    } catch (error) {
      console.warn('Progressive enhancement initialization failed:', error);
    }
  }

  private detectCapabilities(): SystemCapabilities {
    // Memory detection
    let memory: SystemCapabilities['memory'] = 'medium';
    try {
      if ('memory' in performance) {
        const memInfo = (performance as any).memory;
        const totalMB = memInfo.jsHeapSizeLimit / 1024 / 1024;
        if (totalMB < 100) memory = 'low';
        else if (totalMB > 500) memory = 'high';
      }
    } catch (e) {
      console.warn('Memory detection failed:', e);
    }

    // Network detection
    let network: SystemCapabilities['network'] = 'fast';
    try {
      if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') {
          network = 'slow';
        }
      }
    } catch (e) {
      console.warn('Network detection failed:', e);
    }

    // Device detection
    let device: SystemCapabilities['device'] = 'desktop';
    try {
      device = /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
    } catch (e) {
      console.warn('Device detection failed:', e);
    }

    // Performance detection (will be updated dynamically)
    const performanceLevel: SystemCapabilities['performance'] = 'good';

    return { memory, network, device, performance: performanceLevel };
  }

  private initializeEnhancements(): void {
    this.enhancements = [
      {
        id: 'aggressive_caching',
        name: 'Aggressive Caching',
        condition: () => this.capabilities.memory === 'high' && this.capabilities.performance === 'excellent',
        apply: () => {
          smartCacheManager.setPageCache('enhancement_cache_size', 100);
          debugLog('[progressiveEnhancement] aggressive caching enabled');
        },
        rollback: () => {
          smartCacheManager.setPageCache('enhancement_cache_size', 50);
          debugLog('[progressiveEnhancement] aggressive caching disabled');
        },
        priority: 1,
        isActive: false
      },
      {
        id: 'predictive_preloading',
        name: 'Predictive Preloading',
        condition: () => this.capabilities.network === 'fast' && this.capabilities.performance !== 'poor',
        apply: () => {
          predictivePreloader.enable();
          debugLog('[progressiveEnhancement] predictive preloading enabled');
        },
        rollback: () => {
          predictivePreloader.disable();
          debugLog('[progressiveEnhancement] predictive preloading disabled');
        },
        priority: 2,
        isActive: false
      },
      {
        id: 'background_sync',
        name: 'Background Sync',
        condition: () => this.capabilities.performance !== 'poor',
        apply: () => {
          backgroundSyncService.resumeSync();
          debugLog('[progressiveEnhancement] background sync enabled');
        },
        rollback: () => {
          backgroundSyncService.pauseSync();
          debugLog('[progressiveEnhancement] background sync paused');
        },
        priority: 3,
        isActive: false
      },
      {
        id: 'reduced_animations',
        name: 'Reduced Animations',
        condition: () => this.capabilities.performance === 'poor' || this.capabilities.device === 'mobile',
        apply: () => {
          document.body.classList.add('reduce-motion');
          debugLog('[progressiveEnhancement] reduced animations enabled');
        },
        rollback: () => {
          document.body.classList.remove('reduce-motion');
          debugLog('[progressiveEnhancement] reduced animations disabled');
        },
        priority: 4,
        isActive: false
      },
      {
        id: 'lazy_loading',
        name: 'Aggressive Lazy Loading',
        condition: () => this.capabilities.memory === 'low' || this.capabilities.network === 'slow',
        apply: () => {
          document.body.classList.add('aggressive-lazy-loading');
          debugLog('[progressiveEnhancement] aggressive lazy loading enabled');
        },
        rollback: () => {
          document.body.classList.remove('aggressive-lazy-loading');
          debugLog('[progressiveEnhancement] aggressive lazy loading disabled');
        },
        priority: 5,
        isActive: false
      },
      {
        id: 'performance_monitoring',
        name: 'Enhanced Performance Monitoring',
        condition: () => this.capabilities.performance === 'poor',
        apply: () => {
          // Increase monitoring frequency for poor performance
          debugLog('[progressiveEnhancement] performance monitoring enabled');
        },
        rollback: () => {
          debugLog('[progressiveEnhancement] performance monitoring disabled');
        },
        priority: 6,
        isActive: false
      }
    ];
  }

  private startMonitoring(): void {
    this.monitoringInterval = setInterval(() => {
      this.updateCapabilities();
      this.evaluateEnhancements();
    }, 30000); // Check every 30 seconds
  }

  private updateCapabilities(): void {
    const health = performanceMonitor.getSystemHealth();
    
    // Update performance capability based on system health
    if (health.score >= 80) {
      this.capabilities.performance = 'excellent';
    } else if (health.score >= 60) {
      this.capabilities.performance = 'good';
    } else {
      this.capabilities.performance = 'poor';
    }

    // Update memory capability
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      const usedMB = memInfo.usedJSHeapSize / 1024 / 1024;
      if (usedMB > 200) {
        this.capabilities.memory = 'low';
      } else if (usedMB < 50) {
        this.capabilities.memory = 'high';
      } else {
        this.capabilities.memory = 'medium';
      }
    }

    // Update network capability
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      this.capabilities.network = 
        (conn.effectiveType === '4g' || conn.effectiveType === '3g') ? 'fast' : 'slow';
    }
  }

  private evaluateEnhancements(): void {
    // Sort by priority
    const sortedEnhancements = [...this.enhancements].sort((a, b) => a.priority - b.priority);

    sortedEnhancements.forEach(enhancement => {
      const shouldBeActive = enhancement.condition();
      
      if (shouldBeActive && !enhancement.isActive) {
        try {
          enhancement.apply();
          enhancement.isActive = true;
        } catch (error) {
          console.error(`Failed to apply enhancement ${enhancement.id}:`, error);
        }
      } else if (!shouldBeActive && enhancement.isActive) {
        try {
          enhancement.rollback();
          enhancement.isActive = false;
        } catch (error) {
          console.error(`Failed to rollback enhancement ${enhancement.id}:`, error);
        }
      }
    });
  }

  // Manual enhancement control
  forceEnhancement(enhancementId: string, enable: boolean): void {
    const enhancement = this.enhancements.find(e => e.id === enhancementId);
    if (!enhancement) return;

    try {
      if (enable && !enhancement.isActive) {
        enhancement.apply();
        enhancement.isActive = true;
      } else if (!enable && enhancement.isActive) {
        enhancement.rollback();
        enhancement.isActive = false;
      }
    } catch (error) {
      console.error(`Failed to ${enable ? 'enable' : 'disable'} enhancement ${enhancementId}:`, error);
    }
  }

  // Adaptive quality adjustment
  adjustQuality(level: 'low' | 'medium' | 'high'): void {
    const qualityEnhancements = {
      low: ['reduced_animations', 'lazy_loading'],
      medium: ['background_sync'],
      high: ['aggressive_caching', 'predictive_preloading']
    };

    // Disable all quality-related enhancements first
    Object.values(qualityEnhancements).flat().forEach(id => {
      this.forceEnhancement(id, false);
    });

    // Enable enhancements for the selected quality level
    qualityEnhancements[level].forEach(id => {
      this.forceEnhancement(id, true);
    });
  }

  // Emergency performance mode
  enableEmergencyMode(): void {
    console.warn('Enabling emergency performance mode');
    
    // Disable all performance-heavy features
    this.forceEnhancement('aggressive_caching', false);
    this.forceEnhancement('predictive_preloading', false);
    this.forceEnhancement('background_sync', false);
    
    // Enable performance optimizations
    this.forceEnhancement('reduced_animations', true);
    this.forceEnhancement('lazy_loading', true);
    
    // Clear caches to free memory
    smartCacheManager.clearAll();
    predictivePreloader.clearPreloadCache();
  }

  disableEmergencyMode(): void {
    debugLog('[progressiveEnhancement] emergency mode disabled');
    this.evaluateEnhancements(); // Re-evaluate based on current capabilities
  }

  // Public getters
  getCapabilities(): SystemCapabilities {
    return { ...this.capabilities };
  }

  getActiveEnhancements(): Enhancement[] {
    return this.enhancements.filter(e => e.isActive);
  }

  getEnhancementStatus(): { [key: string]: boolean } {
    const status: { [key: string]: boolean } = {};
    this.enhancements.forEach(e => {
      status[e.id] = e.isActive;
    });
    return status;
  }

  // Cleanup
  destroy(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    
    // Rollback all active enhancements
    this.enhancements.forEach(enhancement => {
      if (enhancement.isActive) {
        try {
          enhancement.rollback();
        } catch (error) {
          console.error(`Failed to rollback enhancement ${enhancement.id} during cleanup:`, error);
        }
      }
    });
  }
}

export const progressiveEnhancement = new ProgressiveEnhancement();
export type { Enhancement, SystemCapabilities };
